import { BaseModel } from '../../../../../shared/model/base/BaseModel';
import { CURRENT_USER } from '../../../../../js/resources/SessionObjects';
import { RiskUtils } from '../../../../../shared/utils/RiskUtils';
import { DosedMapModel } from '../../../../../shared/model/mapping/DosedMapModel';
import { CropSeasonStatusModel } from '../../../../../shared/model/timing/CropSeasonStatusModel';

export const localStorageFilterKey = 'CFD_FILTER_GROUP_RISK';

export const recommendationTypes = {
    fungicide: DosedMapModel.ACTION_TYPES.FUNGICIDE_APPLICATION,
    insecticide: DosedMapModel.ACTION_TYPES.INSECTICIDE_APPLICATION,
    pgr: DosedMapModel.ACTION_TYPES.PLANT_GROWTH_REGULATOR_APPLICATION,
    nutrition: DosedMapModel.ACTION_TYPES.FERTILIZER_APPLICATION,
};

export const riskTypes = {
    disease: RiskUtils.TYPES.DISEASE,
    insect: RiskUtils.TYPES.INSECT,
    lodging: RiskUtils.TYPES.LODGING,
    nutrition: RiskUtils.TYPES.NUTRITION,
    abiotic: RiskUtils.TYPES.ABIOTIC
};

export const riskLevels = {
    high: CropSeasonStatusModel.state.HIGH,
    medium: CropSeasonStatusModel.state.MEDIUM,
    low: CropSeasonStatusModel.state.LOW,
    unclassified: CropSeasonStatusModel.state.NO_INFORMATION,
};


/**
 * @class RiskFilterGroupState
 * @extends Backbone.Model
 */
export class RiskFilterGroupState extends Backbone.Model {

    get defaults () {

        return {
            available: {
                recommendationTypes: new Backbone.Collection(),
                riskTypes: new Backbone.Collection(),
                riskLevels: new Backbone.Collection(),
            },

            filtered: {
                recommendationTypes: new Backbone.Collection(),
                riskTypes: new Backbone.Collection(),
                riskLevels: new Backbone.Collection(),
            },

            filteredRecommendationDate: new Backbone.Model({
                start: undefined,
                end: undefined,
            }),

            serializedFilters: this.getDefaultSerializedFilter(),
        };
    }

    getDefaultSerializedFilter() {
        return {
            recommendationTypes: [],
            riskLevels: [],
            riskTypes: [],
            recommendationDate: {
                start: '',
                end: '',
            },
        };
    }

    initialize () {

        const {
            available,
            filtered,
        } = this.attributes;

        const comp = 'sortIdx';

        Object.keys(available).forEach( (key) => {
            available[key].comparator = comp;
        });

        Object.keys(filtered).forEach( (key) => {
            filtered[key].comparator = comp;
        });
    }

    fetchData () {

        return new Promise((resolve) => {

            const {
                available,
                filtered,
            } = this.attributes;


            const transDicRiskLevels = {
                [riskLevels.unclassified]: __('CROSS_FARM_DASHBOARD_LAYOUT.NO_DATA_OR_OUT_OF_SEASON'),
            };

            const transDicRiskTypes = {
                [riskTypes.disease]: __('DISEASE'),
                [riskTypes.insect]: __('PEST'),
                [riskTypes.lodging]: __('LODGING'),
                [riskTypes.nutrition]: __('NUTRITION'),
                [riskTypes.abiotic]: __('PLANT_HEALTH')
            };

            const availableRecommendationTypes = Object.assign({}, recommendationTypes);

            !CURRENT_USER.isFeatureEnabled('CFD_SHOW_PESTS') && delete availableRecommendationTypes.insecticide;
            !CURRENT_USER.isFeatureEnabled('LODGING') && delete availableRecommendationTypes.pgr;
            !CURRENT_USER.isFeatureEnabled('NUTRITION') && delete availableRecommendationTypes.nutrition;
            available.recommendationTypes.reset(this.getModelArray(availableRecommendationTypes));

            const availableRiskTypes = Object.assign({}, riskTypes);

            !CURRENT_USER.isFeatureEnabled('CFD_SHOW_PESTS') && delete availableRiskTypes.insect;
            !CURRENT_USER.isFeatureEnabled('LODGING') && delete availableRiskTypes.lodging;
            !CURRENT_USER.isFeatureEnabled('ABIOTIC') && delete availableRiskTypes.abiotic;
            !CURRENT_USER.isFeatureEnabled('NUTRITION') && delete availableRiskTypes.nutrition;
            available.riskTypes.reset(this.getModelArray(availableRiskTypes, transDicRiskTypes));

            available.riskLevels.reset(this.getModelArray(riskLevels, transDicRiskLevels));

            // reset all filtered collections
            Object.keys(filtered).forEach((key) => {
                filtered[key].reset();
            });

            this.initializeFilter();

            resolve();
        });
    }

    getModelArray(codesObj, transDic) {

        const models = Object.keys(codesObj).map((key, idx) => {

            return new Backbone.Model({
                code: codesObj[key],
                name: transDic && transDic[codesObj[key]] || __(codesObj[key]),
                sortIdx: idx,
            });
        });

        return models;
    }

    initializeFilter () {

        let filters = localStorage.getItem(localStorageFilterKey);

        try {
            const parsed = JSON.parse(filters);

            if(parsed) {

                this.set('serializedFilters', parsed);

                const { 
                    available,
                    filtered,
                } = this.attributes;

                this.setAvailableAndFilteredItems(parsed.recommendationTypes, 'code', available.recommendationTypes, filtered.recommendationTypes);

                this.setAvailableAndFilteredItems(parsed.riskLevels, 'code', available.riskLevels, filtered.riskLevels);
                
                if (parsed.headFilter) {

                    this.attributes.headFilter.set(parsed.headFilter);
                }

                if(parsed.recommendationDate) {

                    const {
                        start,
                        end,
                    } = parsed.recommendationDate;

                    this.attributes.filteredRecommendationDate.set({
                        start: start && moment(start).tz(BaseModel.timezone) || undefined,
                        end: end && moment(end).tz(BaseModel.timezone) || undefined,
                    });
                }
            }
        }
        catch (e) {

            const defaultFilters = this.getDefaultSerializedFilter();

            this.set('serializedFilters', defaultFilters);

            this.removeFilterFromLocalStorage();
        }
    }

    setAvailableAndFilteredItems(array, key, availableItems, filteredItems) {
        if (array.length) {

            array.forEach((val) => {
                let model = availableItems.find({[key]: val});

                availableItems.remove(model);
                filteredItems.add(model);
            });
        }
    }

    resetAllFilters () {

        const {
            available,
            filtered,
            filteredRecommendationDate,
        } = this.attributes;

        Object.keys(available).forEach((key) => {
            this.resetCollection(available[key], filtered[key]);
        });

        filteredRecommendationDate.set({
            start: undefined,
            end: undefined,
        });
    }

    resetCollection(availableCollection, filteredCollection) {
        
        if(filteredCollection.length) {
            availableCollection.add(filteredCollection.models);

            filteredCollection.reset();
        }
    }

    updateFilter () {

        const {
            recommendationTypes,
            riskTypes,
            riskLevels,
        } = this.attributes.filtered;

        const { start, end } = this.attributes.filteredRecommendationDate.attributes;
        
        const serializedFilters = {
            recommendationTypes: recommendationTypes.pluck('code'),
            riskTypes: riskTypes.pluck('code'),
            riskLevels: riskLevels.pluck('code'),
            recommendationDate: {
                start: start && start.valueOf() || '',
                end: end && end.valueOf() || '',
            },
        };

        this.set('serializedFilters', serializedFilters);

        try {
            localStorage.setItem(localStorageFilterKey, JSON.stringify(serializedFilters));
        }
        catch (e) {

            console.error('Error while saving ' + localStorageFilterKey);

            this.removeFilterFromLocalStorage();
        }
    }

    removeFilterFromLocalStorage() {
        try {
            localStorage.removeItem(localStorageFilterKey);
        }
        catch (error) {
            console.error('Error while removing ' + localStorageFilterKey);
        }
    }
}


// WEBPACK FOOTER //
// ./layout/CrossFarmDashboardLayout/Filters/GroupViews/RiskFilterGroupView/RiskFilterGroupState.js